<template>
  <div class="field-preview">
    <div class="field-label">
      <span>{{ field.label }}</span>
      <v-chip x-small color="primary" class="ml-2" v-if="field.required">Required</v-chip>
    </div>

    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      max-width="290px"
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-model="dateDisplay"
          readonly
          placeholder="Select date"
          outlined
          dense
          prepend-icon="mdi-calendar"
          class="preview-input"
          v-bind="attrs"
          v-on="on"
        ></v-text-field>
      </template>
      <v-date-picker
        v-model="date"
        :min="minDateFormatted"
        :max="maxDateFormatted"
        @input="menu = false"
      ></v-date-picker>
    </v-menu>

    <div class="field-description" v-if="field.description">
      <v-icon small color="grey">mdi-information-outline</v-icon>
      <span>{{ field.description }}</span>
    </div>

    <div class="field-constraints mt-1" v-if="hasConstraints">
      <div v-if="field.minDate" class="constraint">
        <v-icon x-small color="grey">mdi-chevron-right</v-icon>
        Min date: {{ formatDateArray(field.minDate) }}
      </div>
      <div v-if="field.maxDate" class="constraint">
        <v-icon x-small color="grey">mdi-chevron-right</v-icon>
        Max date: {{ formatDateArray(field.maxDate) }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DateFieldPreview',
  props: {
    field: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      menu: false,
      date: null
    }
  },
  computed: {
    dateDisplay() {
      return this.date ? new Date(this.date).toLocaleDateString() : '';
    },
    hasConstraints() {
      return !!this.field.minDate || !!this.field.maxDate;
    },
    minDateFormatted() {
      return this.field.minDate ? (typeof this.field.minDate === 'string' ? this.field.minDate : this.arrayToDateString(this.field.minDate)) : null;
    },
    maxDateFormatted() {
      return this.field.maxDate ? (typeof this.field.maxDate === 'string' ? this.field.maxDate : this.arrayToDateString(this.field.maxDate)) : null;
    }
  },
  methods: {
    // Convert date array [year, month, day] to YYYY-MM-DD format for v-date-picker
    arrayToDateString(dateArray) {
      if (!Array.isArray(dateArray) || dateArray.length < 3) {
        return '';
      }

      const [year, month, day] = dateArray;
      // Note: month is 1-indexed in the array but needs to be 0-indexed in Date constructor
      // Adding padding to ensure format is YYYY-MM-DD
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    },
    // Format date for display (handles both arrays and string formats)
    formatDateArray(dateValue) {
      if (typeof dateValue === 'string') {
        // If it's already a string format (YYYY-MM-DD), convert to Date and format
        const date = new Date(dateValue);
        return date.toLocaleDateString();
      }

      if (!Array.isArray(dateValue) || dateValue.length < 3) {
        return 'Invalid date';
      }

      const [year, month, day] = dateValue;
      // Create a date object and format it for display
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString();
    }
  }
}
</script>

<style scoped>
.field-preview {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-weight: 500;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.field-description {
  margin-top: 4px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-constraints {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.constraint {
  display: flex;
  align-items: center;
  gap: 4px;
}

.preview-input {
  opacity: 0.9;
}

.preview-input :deep(.v-input__slot) {
  min-height: 44px;
  cursor: default;
  background-color: #f5f7fa !important;
}
</style>
